<template>
    <v-container fluid>
        <ConfirmDialog
            :is-open="isConfirmComplaintDialogOpen"
            :data="complaint"
            @on-confirm="handleConfirmComplaint(complaint)"
            @on-abort="handleCloseConfirmComplaintDialog"
        >
            <template #title>
                Potwierdzenie zapoznania się z reklamacją
            </template>
            <template #content>
                Czy na pewno chcesz potwierdzić?
            </template>
        </ConfirmDialog>

        <page-title>{{ pageTitle }}</page-title>
        <v-row>
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="handleReturnToComplaintsList"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row tag="form">
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12">
                            <v-card :loading="loading">
                                <TabsNav v-model="tabs" :items="tabItems" />

                                <v-tabs-items v-model="tabs">
                                    <v-divider />
                                    <v-tab-item>
                                        <Details
                                            :complaint="complaint"
                                            :store-module-name="storeModuleName"
                                            :validation="detailsTabValidation"
                                            :has-basic-update-permission="hasBasicUpdatePermission"
                                            :has-update-permission="hasUpdatePermission"
                                            :is-edit-action="currentActionType === ACTION_TYPES.EDIT"
                                            :is-read-only="isComplaintReadOnly"
                                            :is-causes-tab-complete="isCausesTabComplete"
                                            @field-blur="handleFieldBlur"
                                        />
                                    </v-tab-item>
                                    <v-tab-item>
                                        <AttachmentsTab
                                            :complaint="complaint"
                                            :store-module-name="storeModuleName"
                                            :is-read-only="isComplaintReadOnly"
                                        />
                                    </v-tab-item>
                                    <v-tab-item>
                                        <CausesTab
                                            :complaint="complaint"
                                            :store-module-name="storeModuleName"
                                            :validation="causesTabValidation"
                                            :is-read-only="isCausesTabReadOnly"
                                            @field-blur="handleFieldBlur"
                                        />
                                    </v-tab-item>
                                    <v-tab-item>
                                        <RecommendationsTab
                                            :complaint="complaint"
                                            :is-read-only="isComplaintReadOnly"
                                        />
                                    </v-tab-item>
                                    <v-tab-item v-show="tabItems[internalComplaintTabs.EXECUTION].visible">
                                        <ExecutionTab
                                            :complaint="complaint"
                                            :is-read-only="isComplaintReadOnly"
                                        />
                                    </v-tab-item>
                                </v-tabs-items>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="auto">
                            <v-btn
                                color="success"
                                :disabled="isComplaintReadOnly"
                                @click="handleSaveInternalComplaint"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                        <v-col v-if="isConfirmButtonVisible" cols="auto" class="pl-0">
                            <v-btn color="primary" @click="handleShowConfirmComplaintDialog">
                                Zapoznałem się
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios'

// Common
import { errorHandler } from '@/utils/errorHandler'
import { apiConfig } from '@/config'

// Helpers
import initialComplaintData from './helpers/initialComplaintData'
import complaintValidations from './helpers/validation'
import { ACTION_TYPES, PAGE_TITLES, PROCEEDING_TYPES, INTERNAL_COMPLAINT_TABS, createTabItems, STATUSES } from './helpers/constants'
import { mapComplaintResponseToForm, collectComplaintDependencies, buildComplaintPayload } from './helpers/complaint'
import { getFieldErrors } from './helpers/validationErrors'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'InternalComplaintForm',

    components: {
        // Common
        ConfirmDialog: () => import('@/components/common/ConfirmDialog.vue'),
        PageTitle: () => import('@/components/common/PageTitle'),

        // Tabs
        TabsNav: () => import('./Tabs/TabsNav.vue'),
        Details: () => import('./Tabs/DetailsTab/Index.vue'),
        AttachmentsTab: () => import('./Tabs/AttachmentsTab/Index.vue'),
        CausesTab: () => import('./Tabs/CausesTab/Index.vue'),
        RecommendationsTab: () => import('./Tabs/RecommendationsTab/Index.vue'),
        ExecutionTab: () => import('./Tabs/ExecutionTab/Index.vue')
    },

    beforeRouteEnter(to, from, next) {
        const id = to.params.id

        next(async vm => {
            vm.loading = true
            vm.pageTitle = id ? PAGE_TITLES.EDIT : PAGE_TITLES.CREATE
            vm.currentActionType = id ? ACTION_TYPES.EDIT : ACTION_TYPES.CREATE

            try {
                vm.$store.dispatch(`${vm.storeModuleName}/clearStoredValues`)

                if (vm.currentActionType === ACTION_TYPES.EDIT) {
                    const { data } = await vm.$store.dispatch(`${vm.storeModuleName}/getComplaint`, id)

                    await vm.populateComplaintData(data)

                    vm.pageTitle = `${PAGE_TITLES.EDIT} - ${data.complaintNumber}`
                } else {
                    const copiedComplaint = vm.$store.state.complaints.internalComplaints.copiedComplaint

                    if (copiedComplaint) {
                        try {
                            await vm.populateComplaintData(copiedComplaint, { isCopy: true })
                        } finally {
                            vm.$store.commit('complaints/internalComplaints/SET_COPIED_COMPLAINT', null)
                        }
                    } else {
                        const dependencies = collectComplaintDependencies(vm.$store, vm.storeModuleName)

                        await Promise.all(dependencies)
                    }
                }
            } catch (err) {
                errorHandler(err)
            } finally {
                vm.loading = false
            }
        })
    },

    data() {
        return {
            // Constants
            PROCEEDING_TYPES,
            ACTION_TYPES,
            PAGE_TITLES,
            STATUSES,

            // Flags
            isConfirmComplaintDialogOpen: false,

            tabItems: createTabItems(),
            internalComplaintTabs: INTERNAL_COMPLAINT_TABS,
            storeModuleName: 'complaints/internalComplaints',
            loading: false,
            tabs: null,
            pageTitle: '',
            currentActionType: '',
            complaint: initialComplaintData(),
        }
    },

    validations: complaintValidations,

    computed: {
        detailsTabValidation() {
            const complaintValidation = this.$v?.complaint || {}
            const isAdjustment = this.complaint.complaintWayOfProceeding.complaintWayOfProceedingId === this.PROCEEDING_TYPES.ADJUSTMENT

            const mapEntry = (fieldNode, ruleMessages, { skipWhen } = {}) => {
                if (typeof skipWhen === 'function' && skipWhen()) {
                    return {
                        error: false, 
                        messages: [] 
                    }
                }

                return {
                    error: Boolean(fieldNode?.$error),
                    messages: getFieldErrors(fieldNode, ruleMessages)
                }
            }

            return {
                complaintProductionDepartmentId: mapEntry(
                    complaintValidation.complaintProductionDepartmentId, {
                        required: 'Dział jest wymagany' 
                    }
                ),

                jobNumber: mapEntry(
                    complaintValidation.jobNumber, {
                        required: 'Numer zlecenia jest wymagany' 
                    }
                ),

                discrepancyCatcherId: mapEntry(
                    complaintValidation.discrepancyCatcherId, {
                        required: 'Osoba wyłapująca błąd jest wymagana' 
                    }
                ),

                complaintProductDescription: mapEntry(
                    complaintValidation.complaintProductDescription, {
                        required: 'Opis reklamowanej części jest wymagany',
                        minLength: 'Opis reklamowanej części musi mieć minimalnie 3 znaki',
                        maxLength: 'Opis reklamowanej części może mieć maksymalnie 1000 znaków',
                    }
                ),

                responsiblePerson: mapEntry(
                    complaintValidation.responsiblePerson, {
                        required: 'Conajmniej 1 osoba odpowiedzialna jest wymagana',
                        maxLength10: 'Maksymalnie 10 osób odpowiedzialnych',
                    }
                ),

                errorCodeGroupId: mapEntry(
                    complaintValidation.errorCodeGroupId, {
                        required: 'Grupa błędu jest wymagana'
                    }
                ),

                errorCodeId: mapEntry(
                    complaintValidation.errorCodeId, {
                        required: 'Kod błędu jest wymagany'
                    }
                ),
                
                complaintWayOfProceedingId: mapEntry(
                    complaintValidation.complaintWayOfProceeding?.complaintWayOfProceedingId, {
                        required: 'Sposób postępowania jest wymagany' 
                    }
                ),

                complaintWayOfProceedingValue: mapEntry(
                    complaintValidation.complaintWayOfProceeding?.value, {
                        required: 'Ilość roboczogodzin w minutach jest wymagana' }, {
                        skipWhen: () => !isAdjustment,
                    }
                ),
            }
        },

        causesTabValidation() {
            const complaintValidation = this.$v?.complaint || {}

            const mapEntry = (fieldNode, ruleMessages) => ({
                error: Boolean(fieldNode?.$error),
                messages: getFieldErrors(fieldNode, ruleMessages)
            })

            return {
                responsiblePerson: mapEntry(
                    complaintValidation.responsiblePerson, {
                        required: 'Conajmniej 1 osoba odpowiedzialna jest wymagana',
                        maxLength10: 'Maksymalnie 10 osób odpowiedzialnych',
                    }
                ),
            }
        },

        userId() {
            return this.$store.state.user.userId
        },

        hasBasicUpdatePermission() {
            return hasUserPermission('internal_complaints_basic_update')
        },

        hasUpdatePermission() {
            return hasUserPermission('internal_complaints_update')
        },

        hasConfirmPermission() {
            return hasUserPermission('complaints_confirm_update')
        },

        isComplaintReadOnly() {
            const statusId = this.complaint.complaintStatusId

            if (statusId === this.STATUSES.NEW) {
                return false
            }

            return !this.hasUpdatePermission
        },

        isCausesTabReadOnly() {
            const statusId = this.complaint.complaintStatusId

            if (statusId === this.STATUSES.NEW || statusId === this.STATUSES.IN_PROGRESS) {
                return false
            }

            return !this.hasUpdatePermission
        },

        isCausesTabComplete() {
            const cause = this.complaint.discrepancyCause || ''
            const errorId = this.complaint.complaintErrorId
            const responsible = this.complaint.responsiblePerson

            const hasCause = cause.length > 0
            const hasError = errorId !== null
            const hasResponsible = responsible?.length > 0

            return hasCause && hasError && hasResponsible
        },

        shouldDisplayExecutionTab() {
            return this.hasBasicUpdatePermission && this.currentActionType === this.ACTION_TYPES.EDIT
        },

        isConfirmed() {
            return this.complaint.responsiblePersonRead?.find(item => item.id === this.userId)?.confirm 
        },

        isResponsiblePersonRead() {
            return this.complaint.responsiblePersonRead?.find(item => item.id === this.userId)
        },

        isConfirmButtonVisible() {
            return this.complaint.complaintStatusId === this.STATUSES.ACCEPTED 
                && this.isCausesTabComplete 
                && !this.isConfirmed 
                && this.hasConfirmPermission 
                && this.isResponsiblePersonRead
        }
    },

    watch: {
        shouldDisplayExecutionTab: {
            immediate: true,
            handler(isVisible) {
                this.setTabVisibility(this.internalComplaintTabs.EXECUTION, isVisible)
            }
        }
    },

    mounted() {
        if (this.currentActionType === this.ACTION_TYPES.EDIT) {
            this.$store.state.complaints.internalComplaints.users = [this.complaint.discrepancyCatcher]
        }
    },

    methods: {
        async populateComplaintData(data, {
            isCopy = false 
        } = {}) {
            const {
                complaint,
                responsibleUsers,
                discrepancyCatcherUser,
            } = mapComplaintResponseToForm(this.complaint, data, { isCopy })

            this.complaint = complaint

            if (Array.isArray(responsibleUsers)) {
                this.$store.state.complaints.internalComplaints.responsibleUsers = responsibleUsers
            }

            if (discrepancyCatcherUser) {
                this.$store.state.complaints.internalComplaints.users = [discrepancyCatcherUser]
            }

            const dependencies = collectComplaintDependencies(this.$store, this.storeModuleName, data)

            if (isCopy && Array.isArray(this.complaint.complaintAttachment) && this.complaint.complaintAttachment.length > 0) {
                try {
                    await this.duplicateComplaintAttachments()
                } catch (error) {
                    errorHandler(error)
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Nie udało się skopiować załączników. Dodaj je ponownie ręcznie.'
                    })
                }
            }

            await Promise.all(dependencies)
        },

        setTabVisibility(tabIndex, isVisible) {
            const tabItem = this.tabItems[tabIndex]

            if (!tabItem || tabItem.visible === isVisible) {
                return
            }

            this.$set(this.tabItems[tabIndex], 'visible', isVisible)

            if (!isVisible && this.tabs === tabIndex) {
                this.tabs = this.getFirstVisibleTabIndex()
            }
        },

        getFirstVisibleTabIndex() {
            const index = this.tabItems.findIndex(item => item.visible !== false)

            return index === -1 ? null : index
        },

        handleReturnToComplaintsList() {
            this.$router.push({ name: 'complaintsComplaints'})
        },

        handleFieldBlur(field) {
            const handlers = {
                complaintProductionDepartmentId: () => this.$v.complaint.complaintProductionDepartmentId.$touch(),
                jobNumber: () => this.$v.complaint.jobNumber.$touch(),
                discrepancyCatcherId: () => this.$v.complaint.discrepancyCatcherId.$touch(),
                complaintProductDescription: () => this.$v.complaint.complaintProductDescription.$touch(),
                responsiblePerson: () => this.$v.complaint.responsiblePerson.$touch(),
                errorCodeGroupId: () => {
                    this.$v.complaint.errorCodeGroupId.$touch()
                    this.$v.complaint.errorCodeId.$reset()
                },
                errorCodeId: () => this.$v.complaint.errorCodeId.$touch(),
                complaintWayOfProceedingId: () => this.$v.complaint.complaintWayOfProceeding.complaintWayOfProceedingId.$touch(),
                complaintWayOfProceedingValue: () => this.$v.complaint.complaintWayOfProceeding.value.$touch(),
            }

            const handler = handlers[field]

            if (handler) handler()
        },

        handleSaveInternalComplaint() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', { type: 'error', message: 'Formularz zawiera błędy.' })

                return
            }

            const statusId = this.complaint.complaintStatusId

            if (statusId === this.STATUSES.ACCEPTED && !this.isCausesTabComplete) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można zmienić statusu na "Uznana" bez uzupełnienia wszystkich pól w zakładce "Przyczyny".'
                })

                return
            }

            const payload = buildComplaintPayload(this.complaint)
            
            let method = ''

            switch (this.currentActionType) {
                case this.ACTION_TYPES.EDIT:
                    method = 'updateComplaint'
                    break
                case this.ACTION_TYPES.CREATE:
                    method = 'createComplaint'
                    break
            }

            this.$store.dispatch(`${this.storeModuleName}/${method}`, payload)
                .then(() => {
                    this.$router.push({ name: 'complaintsComplaints' })
                })
                .catch(err => errorHandler(err))
        },

        async duplicateComplaintAttachments() {
            const attachments = Array.isArray(this.complaint.complaintAttachment)
                ? [...this.complaint.complaintAttachment]
                : []

            if (attachments.length === 0) {
                return
            }

            const duplicatedAttachments = []

            for (const attachment of attachments) {
                const duplicated = await this.cloneComplaintAttachment(attachment)

                duplicatedAttachments.push(duplicated)
            }

            this.complaint.complaintAttachment = duplicatedAttachments
        },

        async cloneComplaintAttachment(attachment) {
            const filePath = attachment?.file

            if (!filePath) {
                throw new Error('Brak ścieżki do pliku załącznika.')
            }

            const fileUrl = filePath.startsWith('http')
                ? filePath
                : `${apiConfig.FACTORYBASE_API_URI || ''}${filePath}`

            const fileName = this.extractAttachmentFileName(attachment)

            const response = await axios.get(fileUrl, { responseType: 'blob' })
            const blob = response.data

            const formData = new FormData()

            formData.append('file', blob, fileName)

            const uploadResponse = await this.$store.dispatch(`${this.storeModuleName}/createAttachment`, {
                formData,
                fileName
            })

            const uploadedAttachment = uploadResponse?.data

            if (!uploadedAttachment) {
                throw new Error('Brak danych nowego załącznika po skopiowaniu.')
            }

            return uploadedAttachment
        },

        extractAttachmentFileName(attachment) {
            if (!attachment) {
                return 'attachment'
            }

            if (attachment.fileName) {
                return attachment.fileName
            }

            if (attachment.name) {
                return attachment.name
            }

            const filePath = attachment.file || ''
            const segments = filePath.split('/')

            return segments.pop() || 'attachment'
        },

        handleConfirmComplaint() {
            const payload = buildComplaintPayload(this.complaint)

            this.$store.dispatch('complaints/updateComplaint', {
                ...payload,
                confirm: true
            })
                .then(() => {
                    this.handleCloseConfirmComplaintDialog()
                    this.$router.push({ name: 'complaintsComplaints'})
                })
        },

        handleShowConfirmComplaintDialog() {
            this.isConfirmComplaintDialogOpen = true
        },

        handleCloseConfirmComplaintDialog() {
            this.isConfirmComplaintDialogOpen = false
        }
    }
}
</script>
